import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { generateBillingPeriods } from '../../utils/billingPeriods';
import { buildingsApi } from '../../api/buildingsApi';
import { apartmentsApi } from '../../api/apartmentsApi';
import { parkingManagementApi } from '../../api/parkingManagementApi';
import { AccessCardSearchPicker } from '../apartments/AccessCardSearchPicker';
import {
  getFeeCategoryDisplayUnit,
  usesPerInvoiceQuantity,
} from '../../utils/feeQuantity';
import {
  getAccessCardKindForFeeCode,
  getAccessCardPickerPurposeForFee,
  isCardIssuanceFeeCode,
} from '../../utils/accessCardFee';
import { isKtdvContractFeeCode } from '../../utils/feeCategoryCodeGrouping';
import { isChargingFeeCategory } from '../../utils/usageFeeCategoryMatching';
import { contractsApi } from '../../api/contractsApi';

interface AddFixedServiceFeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceType: string;
  feeCategory: any;
  apartmentId: string;
  buildingId?: string;
  onSave: (data: any) => Promise<void>;
}

const AddFixedServiceFeeModal: React.FC<AddFixedServiceFeeModalProps> = ({
  isOpen,
  onClose,
  serviceType,
  feeCategory,
  apartmentId,
  buildingId,
  onSave,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [billingPeriod, setBillingPeriod] = useState(() => {
    const periods = generateBillingPeriods();
    return periods[0]?.value || '';
  });
  const [fromDate, setFromDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [selectedAccessCardId, setSelectedAccessCardId] = useState('');
  const [hasAvailableCards, setHasAvailableCards] = useState(false);
  const [cardsChecking, setCardsChecking] = useState(false);
  const [cardsLoadError, setCardsLoadError] = useState<string | null>(null);
  const [contractOptions, setContractOptions] = useState<Array<{
    id: string;
    code: string;
    name: string;
    customerName?: string | null;
    amountPerPeriod: number;
  }>>([]);
  const [selectedContractId, setSelectedContractId] = useState('');
  const [contractsLoading, setContractsLoading] = useState(false);

  const [buildingSettings, setBuildingSettings] = useState<any>(null);

  const billingPeriods = generateBillingPeriods();

  const fee = feeCategory?.feeCategory;
  const perInvoiceQuantity = fee ? usesPerInvoiceQuantity(fee) : false;
  const isChargingFee = fee ? isChargingFeeCategory(fee) : false;
  const unitLabel = fee ? getFeeCategoryDisplayUnit(fee) : 'đơn vị';
  const storedQuantity = feeCategory?.quantity || 1;
  const feeCode = fee?.feeCode;
  const isCardFee = isCardIssuanceFeeCode(feeCode);
  const isKtdvFee = isKtdvContractFeeCode(feeCode);
  const combinedElevatorVehicleCards = Boolean(buildingSettings?.combinedElevatorVehicleCards);
  const cardKind = getAccessCardKindForFeeCode(feeCode, { combinedElevatorVehicleCards });
  const cardPickerPurpose = getAccessCardPickerPurposeForFee(feeCode, combinedElevatorVehicleCards);
  const resolvedBuildingId = buildingId || buildingSettings?.id;

  const showCardSelection =
    isCardFee && !!cardKind && !!resolvedBuildingId && hasAvailableCards;

  const cardKindLabel =
    cardKind === 'VEHICLE'
      ? 'Thẻ xe'
      : cardKind === 'ELEVATOR'
        ? 'Thẻ thang máy'
        : cardKind === 'ENTRY'
          ? 'Thẻ ra vào'
          : 'Thẻ';

  const selectedContract = contractOptions.find((c) => c.id === selectedContractId);

  const calculatedAmount = useMemo(() => {
    if (isKtdvFee && selectedContract) {
      return selectedContract.amountPerPeriod;
    }
    if (!fee) return 0;

    const qty = showCardSelection
      ? 1
      : perInvoiceQuantity || isChargingFee
        ? Math.max(0, parseFloat(quantity) || 0) || 1
        : storedQuantity;

    const isQuotaBased =
      fee.pricingType === 'QUOTA_BASED' || feeCategory?.pricingType === 'QUOTA_BASED';

    if (isQuotaBased) {
      const quotaLimit = fee.quotaLimit ?? feeCategory?.quotaLimit ?? 0;
      const f1 = fee.quotaPrice ?? 0;
      const f2 = fee.overQuotaPrice1 ?? 0;
      const within = Math.min(qty, quotaLimit);
      const over1 = Math.max(qty - within, 0);
      return within * f1 + over1 * f2;
    }

    const basePrice = fee.basePrice || 0;
    const vatPercentage = fee.vatPercentage || 0;
    const baseAmount = basePrice * qty;
    let total = baseAmount * (1 + vatPercentage / 100);
    if (isChargingFee) {
      const day = new Date().getDate();
      if (day > 15) {
        total *= 0.5;
      }
    }
    return total;
  }, [fee, feeCategory, perInvoiceQuantity, quantity, storedQuantity, showCardSelection, isKtdvFee, selectedContract, isChargingFee]);

  const loadContracts = useCallback(async () => {
    if (!isKtdvFee || !apartmentId || !feeCategory?.feeCategoryId) {
      setContractOptions([]);
      return;
    }
    try {
      setContractsLoading(true);
      const list = await contractsApi.listForInvoice(apartmentId, feeCategory.feeCategoryId);
      setContractOptions(list);
      setSelectedContractId(list[0]?.id ?? '');
    } catch {
      setContractOptions([]);
      setSelectedContractId('');
    } finally {
      setContractsLoading(false);
    }
  }, [isKtdvFee, apartmentId, feeCategory?.feeCategoryId]);

  const checkAvailableCards = useCallback(async () => {
    if (!resolvedBuildingId || !cardKind || !isCardFee) {
      setHasAvailableCards(false);
      setCardsLoadError(null);
      return;
    }
    try {
      setCardsChecking(true);
      setCardsLoadError(null);
      const { count } = await parkingManagementApi.countAvailableAccessCards(
        resolvedBuildingId,
        cardKind,
        cardPickerPurpose,
      );
      setHasAvailableCards(count > 0);
    } catch (err: any) {
      setHasAvailableCards(false);
      setCardsLoadError(
        err.response?.data?.message ||
          'Không tải được danh sách thẻ. Kiểm tra dữ liệu ở Quản lý thẻ hoặc cập nhật hệ thống.',
      );
    } finally {
      setCardsChecking(false);
    }
  }, [resolvedBuildingId, cardKind, cardPickerPurpose, isCardFee]);

  useEffect(() => {
    if (isOpen && apartmentId) {
      loadBuildingSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, apartmentId]);

  useEffect(() => {
    if (buildingSettings && billingPeriod) {
      calculateDateRanges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingSettings, billingPeriod]);

  useEffect(() => {
    if (isOpen && isKtdvFee) {
      loadContracts();
    }
  }, [isOpen, isKtdvFee, loadContracts]);

  useEffect(() => {
    if (isOpen) {
      setQuantity('1');
      setSelectedAccessCardId('');
      setSelectedContractId('');
      setCardsLoadError(null);
      setHasAvailableCards(false);
    }
  }, [isOpen, feeCategory?.feeCategoryId]);

  useEffect(() => {
    if (!isOpen || !isCardFee) return;
    checkAvailableCards();
  }, [isOpen, isCardFee, checkAvailableCards]);

  useEffect(() => {
    if (showCardSelection) {
      setQuantity('1');
    }
  }, [showCardSelection, selectedAccessCardId]);

  const loadBuildingSettings = async () => {
    if (!apartmentId) return;

    try {
      const apartment = await apartmentsApi.getApartmentById(apartmentId);
      const building = await buildingsApi.getBuilding(apartment.buildingId);
      setBuildingSettings(building);
    } catch (err) {
      console.error('Failed to load building settings:', err);
    }
  };

  const calculateDateRanges = () => {
    if (!buildingSettings || !billingPeriod) {
      return;
    }

    try {
      const periodMatch = billingPeriod.match(/(\d{2})\/(\d{4})/);
      if (!periodMatch) {
        return;
      }

      const [, month, year] = periodMatch;
      const monthNum = parseInt(month);
      const yearNum = parseInt(year);

      if (isNaN(monthNum) || isNaN(yearNum) || monthNum < 1 || monthNum > 12) {
        return;
      }

      const fromDay = Math.max(1, Math.min(31, buildingSettings.paymentFromDay || 10));
      const fromDateObj = new Date(Date.UTC(yearNum, monthNum - 1, fromDay));

      const toDay = Math.max(1, Math.min(31, buildingSettings.paymentToDay || 20));
      const dueDateObj = new Date(Date.UTC(yearNum, monthNum - 1, toDay));

      if (isNaN(fromDateObj.getTime()) || isNaN(dueDateObj.getTime())) {
        return;
      }

      setFromDate(fromDateObj.toISOString().split('T')[0]);
      setDueDate(dueDateObj.toISOString().split('T')[0]);
    } catch (dateError) {
      console.error('Error calculating date ranges:', dateError);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const qty = showCardSelection
      ? 1
      : Math.max(0, parseFloat(quantity) || 0);

    if (showCardSelection && !selectedAccessCardId) {
      setError('Vui lòng chọn thẻ từ danh sách');
      return;
    }

    if (perInvoiceQuantity && qty <= 0) {
      setError('Vui lòng nhập số lượng lớn hơn 0');
      return;
    }

    if (isKtdvFee && !selectedContractId) {
      setError('Vui lòng chọn hợp đồng');
      return;
    }

    setLoading(true);

    try {
      const data = {
        apartmentId,
        feeCategoryId: feeCategory?.feeCategoryId,
        billingPeriod,
        amount: Math.round(calculatedAmount),
        quantity: perInvoiceQuantity || showCardSelection || isChargingFee ? qty : undefined,
        accessCardId: showCardSelection ? selectedAccessCardId : undefined,
        contractId: isKtdvFee ? selectedContractId : undefined,
        fromDate,
        dueDate,
        serviceType,
        type: 'FIXED',
      };

      await onSave(data);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Có lỗi xảy ra khi thêm hóa đơn');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    const periods = generateBillingPeriods();
    setBillingPeriod(periods[0]?.value || '');
    setFromDate('');
    setDueDate('');
    setQuantity('1');
    setSelectedAccessCardId('');
    setCardsLoadError(null);
    setHasAvailableCards(false);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const bannerText = isKtdvFee
    ? `${serviceType} (chọn hợp đồng để lấy số tiền theo kỳ)`
    : perInvoiceQuantity
    ? `${serviceType} (phí phát sinh theo số lượng)`
    : fee?.autoCloseMonthly
      ? `${serviceType} (phí cố định hàng tháng)`
      : `${serviceType} (phí cố định)`;

  const displayQuantity = showCardSelection
    ? 1
    : perInvoiceQuantity || isChargingFee
      ? Math.max(0, parseFloat(quantity) || 0) || 1
      : storedQuantity;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Thêm hóa đơn phí cố định
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4">
          <div className="bg-blue-100 border border-blue-400 text-blue-700 px-3 py-2 rounded text-sm">
            {bannerText}
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Loại phí dịch vụ
            </label>
            <input
              type="text"
              value={serviceType}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kỳ hóa đơn
            </label>
            <select
              value={billingPeriod}
              onChange={(e) => setBillingPeriod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              {billingPeriods.map((period) => (
                <option key={period.value} value={period.value}>
                  {period.label}
                </option>
              ))}
            </select>
          </div>

          {isKtdvFee && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hợp đồng *
              </label>
              {contractsLoading ? (
                <div className="text-sm text-gray-500">Đang tải hợp đồng...</div>
              ) : contractOptions.length === 0 ? (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  Không có hợp đồng thu đã ký cho căn hộ này và danh mục phí này.
                </div>
              ) : (
                <select
                  value={selectedContractId}
                  onChange={(e) => setSelectedContractId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {contractOptions.map((contract) => (
                    <option key={contract.id} value={contract.id}>
                      {contract.code} — {contract.name}
                      {contract.customerName ? ` (${contract.customerName})` : ''}
                      {' — '}
                      {new Intl.NumberFormat('vi-VN').format(contract.amountPerPeriod)} đ/kỳ
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {showCardSelection ? (
            <AccessCardSearchPicker
              buildingId={resolvedBuildingId!}
              kind={cardKind!}
              purpose={cardPickerPurpose}
              value={selectedAccessCardId}
              onChange={(cardId) => setSelectedAccessCardId(cardId)}
            />
          ) : (
            (perInvoiceQuantity || isChargingFee) && !isKtdvFee && (
              <div>
                {isCardFee && (
                  <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    {cardsChecking
                      ? `Đang kiểm tra ${cardKindLabel}...`
                      : cardsLoadError
                        ? cardsLoadError
                        : `Chưa có ${cardKindLabel} khả dụng trong Quản lý thẻ (chưa thêm thẻ hoặc đã bán hết). Nhập số lượng thủ công bên dưới.`}
                  </div>
                )}
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Số lượng ({unitLabel})
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Thành tiền = đơn giá × số lượng
                </p>
              </div>
            )
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hạn thanh toán từ ngày
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Đến ngày
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {feeCategory && (
            <div className="bg-gray-50 p-3 rounded-md">
              <div className="text-sm text-gray-600 mb-2">
                <strong>Thông tin tính phí:</strong>
              </div>
              <div className="text-sm space-y-1">
                <div>
                  Số lượng: {isKtdvFee ? 1 : displayQuantity} {unitLabel}
                </div>
                {!isKtdvFee && (
                  <div>
                    Đơn giá: {new Intl.NumberFormat('vi-VN').format(fee?.basePrice || 0)} VNĐ / {unitLabel}
                  </div>
                )}
                {isKtdvFee && selectedContract && (
                  <div>Số tiền theo hợp đồng / kỳ</div>
                )}
                {!isKtdvFee && <div>VAT: {fee?.vatPercentage || 0}%</div>}
                <div className="font-medium border-t pt-2">
                  Thành tiền: {new Intl.NumberFormat('vi-VN').format(Math.round(calculatedAmount))} VNĐ
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
            >
              {loading ? 'Đang lưu...' : 'Lưu'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddFixedServiceFeeModal;

